import Immutable from 'immutable';
import Request from 'core/api/Request';
import {
  FAILURE_REASON,
  NETWORK_BACKOFF_INTERVAL,
  NETWORK_MAX_RETRIES,
} from './constants';

// superagent error handling (core/api/Request is a wrapper around superagent):
//   see: https://visionmedia.github.io/superagent/#error-handling
//   note: we are using a super old version of superagent and some weird thing that hacks promises
//      into that old version. They're supported in the new one. So take the docs with a grain of salt,
//      and always test manually against a real server.
//   errors:
//      superagent throws on any 3xx, 4xx, 5xx.
//      we only throw on network errors (to aid in retrying)
//      server errors should return a failed thing
//   response.ok:
//      if you error on all the bad things, what is response.ok for? I don't know, but it's there
//      so ¯\_(ツ)_/¯

const isCsrfError = (err) => {
  const csrfResponseBody1 = JSON.stringify({
    detail: 'CSRF Failed: CSRF token missing or incorrect.',
  });
  const csrfResponseBody2 = JSON.stringify({
    detail: 'CSRF Failed: CSRF cookie not set.',
  });
  const responseBody = JSON.stringify(err.response?.body);

  return (
    responseBody === csrfResponseBody1 || responseBody === csrfResponseBody2
  );
};

// const loggedOutError = {"detail":"Authentication credentials were not provided."}
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const requestCsrf = async () => {
  // Requesting the csrf token will set the cookie automatically
  let retries = 2;
  const retryCSRF = async () => {
    const response = await Request.get('/api/v1/csrf/');
    const csrf = response?.body?.csrf_token;
    if (csrf) {
      return csrf;
    } else if (retries) {
      retries -= 1;
      await sleep(NETWORK_BACKOFF_INTERVAL);
      return await retryCSRF();
    } else {
      throw new Error('Could not refresh csrf');
    }
  };

  return await retryCSRF();
};

const buildRequest = (method, url, body, transformRequest) => {
  let request = Request[method](url);

  request = transformRequest(request);
  // console.log('transformedRequest', request);

  if (body) {
    request = request.send(body);
  }

  return request;
};

const handleRequest = async ({
  method,
  url,
  body,
  transformRequest = (r) => r,
  transformBody = (b) => b,
  requestAttemptCount = 0,
  csrfFailureCount = 0,
}) => {
  const MAX_CSRF_RETRIES = 3; // not including original post
  let response;

  if (typeof transformRequest !== 'function') {
    throw new Error('transformRequest must be a function');
  }

  if (typeof transformBody !== 'function') {
    throw new Error('transformBody must be a function');
  }

  const request = buildRequest(method, url, body, transformRequest);

  const backoffAndRetry = async () => {
    await sleep(NETWORK_BACKOFF_INTERVAL * 2 ** requestAttemptCount);

    return handleRequest({
      method,
      url,
      body,
      transformRequest,
      transformBody,
      requestAttemptCount: requestAttemptCount + 1,
      csrfFailureCount,
    });
  };

  try {
    response = await request;
  } catch (err) {
    if (!err.status) {
      // likely network error (lost internet?)
      if (requestAttemptCount < NETWORK_MAX_RETRIES) {
        return await backoffAndRetry();
      }
      return {
        ok: false,
        failureReason: FAILURE_REASON.NETWORK_ERROR,
        body: Immutable.Map(),
        status: null,
      };
    }

    if (isCsrfError(err) && csrfFailureCount < MAX_CSRF_RETRIES) {
      await requestCsrf();
      const timeToSleep = 1000 * 2 ** csrfFailureCount; // 1s, 2s, 4s
      await sleep(timeToSleep);
      return await handleRequest({
        method,
        url,
        body,
        transformRequest,
        transformBody,
        requestAttemptCount,
        csrfFailureCount: csrfFailureCount + 1,
      });
    }

    // Enrolinator returns 412 for errors
    if (err.status === 412) {
      const responseBody = transformBody(
        Immutable.fromJS(err.response?.body || {})
      );

      return {
        ok: false,
        failureReason: responseBody.message || '',
        body: responseBody,
        status: err.status,
      };
    }

    if (err.status === 400) {
      return {
        ok: false,
        failureReason: FAILURE_REASON.BAD_REQUEST,
        body: Immutable.fromJS(err.response.body || {}),
        status: err.status,
      };
    }

    if (err.status === 403 || err.status === 401) {
      return {
        ok: false,
        failureReason: FAILURE_REASON.UNAUTHORIZED_ERROR,
        body: Immutable.fromJS(err.response?.body || {}),
        status: err.status,
      };
    }

    if (err.status === 404) {
      return {
        ok: false,
        failureReason: FAILURE_REASON.NOT_FOUND_ERROR,
        body: Immutable.Map(),
        status: err.status,
      };
    }

    if (err.status === 504) {
      if (requestAttemptCount < NETWORK_MAX_RETRIES) {
        return await backoffAndRetry();
      }
      return {
        ok: false,
        failureReason: FAILURE_REASON.GATEWAY_TIMEOUT,
        body: Immutable.Map(),
        status: err.status,
      };
    }

    if (requestAttemptCount < NETWORK_MAX_RETRIES) {
      return await backoffAndRetry();
    }

    // any other error is a server error
    const responseBody = transformBody(
      Immutable.fromJS(err.response?.body || {})
    );
    return {
      ok: false,
      failureReason: responseBody.message || FAILURE_REASON.SYSTEM_ERROR,
      body: responseBody,
      status: err.status,
    };
  }

  try {
    if (!response) {
      throw new Error(
        'response is falsey. something is wrong with the request error handling'
      );
    }
    if (!response.ok) {
      throw new Error(
        "response is not ok but didn't error. superagent did something weird"
      );
    }

    return {
      ok: true,
      failureReason: '',
      body: transformBody(Immutable.fromJS(response?.body || {})),
      status: response.status,
    };
  } catch (err) {
    // this should only happen if there's a problem in our javascript
    global.Sentry?.captureException(err, {
      extra: {
        response,
      },
    });

    return {
      ok: false,
      failureReason: FAILURE_REASON.SYSTEM_ERROR,
      body: Immutable.Map(),
      status: err.status,
    };
  }
};

// helpers
export const get = async (url, options = {}) =>
  handleRequest({
    method: 'get',
    url,
    ...options,
  });

export const post = async (url, body, options = {}) =>
  handleRequest({
    method: 'post',
    url,
    body,
    ...options,
  });

export const patch = async (url, body, options = {}) =>
  handleRequest({
    method: 'patch',
    url,
    body,
    ...options,
  });

// TODO: use these
// export const put = async (url, body, options = {}) =>
//   handleRequest({
//     method: 'put',
//     url,
//     body,
//     ...options,
//   });
//
// export const del = async (url, body, options = {}) =>
//   handleRequest({
//     method: 'delete',
//     url,
//     ...options,
//   });
//
// export const head = async (url, body, options = {}) =>
//   handleRequest({
//     method: 'head',
//     url,
//     ...options,
//   });
